import { Axial, axialKey, neighbor } from './HexCoordinates';
import { HexCellData } from './HexSurface';

/**
 * What the ground allows, client-side.
 *
 * **Twin of `players.rs`.** The server decides every step that actually
 * happens; this exists so the client can show what it is about to ask for —
 * the pawn's pace between steps, and the route drawn under the cursor before
 * anything is clicked. The two tables have to say the same thing, because the
 * difference between them is visible: a route drawn through a thicket the
 * server refuses, or a pawn that arrives before its step does.
 *
 * Nothing here decides anything. A move is legal when the server says so, and
 * the route that gets walked is the one it sends back.
 */

/**
 * Levels of difference a step may not cross. Twin of `MAX_STEP`.
 *
 * **Dwa**, czyli wolno wejść na różnicę jednego poziomu. Pomiar stojący przy
 * bliźniaku w `players.rs` mówi, co to robi z mapą: 2,7% lądu osiągalne zamiast
 * połowy. Obie liczby muszą ruszać się razem — podgląd trasy rysowany po tej
 * stronie i odmowa po tamtej muszą mówić to samo.
 */
export const MAX_STEP = 2;

/** Above this level of woodland or stone nothing gets through. */
const PASSABLE_LEVEL = 2;

/**
 * Seconds between steps on clear ground. Twin of `MOVE_COOLDOWN`.
 *
 * 0,50 zamiast 0,30 — gra chodzi o dwie piąte wolniej. To jest **jedna liczba
 * po każdej stronie drutu** i obie muszą ruszać się razem: pionek przechodzi
 * pole w tym tempie, a serwer w tym samym tempie pozwala na następny krok.
 *
 * Pół sekundy to równe dziesięć taktów tickera chodzenia (50 ms), więc krok
 * przychodzi wtedy, kiedy zapowiada — patrz `MOVE_COOLDOWN` w `players.rs`,
 * gdzie stoi rachunek i pomiar z czasów, gdy trafienie w siatkę zegara nie
 * wyszło.
 */
export const STEP_SECONDS = 0.5;

/** What the ground takes off the pace. Twin of the penalties in `players.rs`. */
const STONE_PENALTY = 0.2;
const FOREST_PENALTY = 0.15;
const SLOWEST = 0.25;

/** Richness as the one to five the client draws and the server walks. */
export function levelOf(richness: number | undefined): number {
  if (!richness) return 0;
  return 1 + Math.round((Math.min(richness, 255) / 255) * 4);
}

/**
 * Ile szybciej idzie się gotową drogą. Bliźniak `roads.speed` z katalogu.
 *
 * Wpisany tu, a nie czytany z katalogu, z tego samego powodu, dla którego są tu
 * kary za las i kamień: ta funkcja liczy trasę na każde drgnięcie kursora
 * i musi odpowiadać, zanim cokolwiek dojedzie. **Ten plik nazywa to długiem
 * i nadal nim jest** — obie liczby muszą ruszać się razem.
 */
const ROAD_PACE = 1.5;

/** How fast somebody crosses this cell, against one on clear ground. */
export function paceOn(cell: HexCellData | undefined): number {
  if (!cell) return 1;

  let pace = 1;
  if (cell.stone) pace -= STONE_PENALTY;
  if (cell.forest) pace -= FOREST_PENALTY;
  return Math.max(SLOWEST, pace);
}

/** How long standing on this cell delays the next step, in seconds. */
export function crossingSeconds(cell: HexCellData | undefined): number {
  // A finished road replaces what grows on the cell rather than adding to it,
  // exactly as `speed` does on the server: a road through a wood is a way past
  // the wood. An unfinished one carries nobody faster, so it is not a road yet.
  if (cell?.road && !cell.roadPending) return STEP_SECONDS / ROAD_PACE;
  return STEP_SECONDS / paceOn(cell);
}

/**
 * Czy krok z pola na pole idzie **wzdłuż** rzeki.
 *
 * Droga może rzekę przeciąć i nie może się z nią pokryć: wstęga położona na
 * wstędze to dwie rzeczy w jednym miejscu, z których widać jedną. Krawędź jest
 * tu jednostką, bo to ona jest wspólna — rzeka i droga przechodzą przez tę samą
 * granicę pól albo przez dwie różne.
 */
export function alongRiver(from: HexCellData | undefined, direction: number): boolean {
  return !!from?.river && (from.river & (1 << direction)) !== 0;
}

/** Whether a cell can be stood on at all: not water, not a thicket. */
export function habitable(cell: HexCellData | undefined): cell is HexCellData {
  if (!cell) return false;
  if (cell.elevation < 0 || cell.lakeLevel !== undefined) return false;
  return levelOf(cell.forest) <= PASSABLE_LEVEL && levelOf(cell.stone) <= PASSABLE_LEVEL;
}

/** Lustro wody na polu, w poziomach, albo nic na lądzie. Bliźniak `players::mirror`. */
export function mirrorOf(cell: HexCellData): number | undefined {
  if (cell.lakeLevel !== undefined) return cell.lakeLevel;
  if (cell.elevation < 0) return 0;
  return undefined;
}

/**
 * Maska mostu na polu: droga na wodzie. Zero na lądzie i bez mostu.
 *
 * Most w budowie nikogo nie niesie — chyba że pytanie dotyczy kładzenia drogi,
 * bo droga do mostu, który stanie za minutę, jest drogą do mostu.
 */
export function bridgeOn(cell: HexCellData | undefined, pendingCounts = false): number {
  if (!cell || mirrorOf(cell) === undefined) return 0;
  if (cell.roadPending && !pendingCounts) return 0;
  return cell.road ?? 0;
}

/**
 * Whether a step from one cell to the next is allowed.
 *
 * **Z kierunkiem zna mosty** — bliźniak `players::crossable`: na pole wody
 * z mostem wchodzi się jego końcem, z mostu schodzi się jego końcem, a jego
 * wysokością jest lustro wody. Bez kierunku to dawna reguła: woda nie do wejścia.
 */
export function passable(
  from: HexCellData | undefined,
  to: HexCellData | undefined,
  direction?: number,
  pendingCounts = false
): boolean {
  if (!from || !to) return false;
  if (direction === undefined) {
    if (!habitable(to)) return false;
    return Math.abs(to.elevation - from.elevation) < MAX_STEP;
  }

  const bridgeTo = bridgeOn(to, pendingCounts);
  let there = to.elevation;
  if (mirrorOf(to) !== undefined) {
    if ((bridgeTo & (1 << ((direction + 3) % 6))) === 0) return false;
    there = mirrorOf(to)!;
  } else if (!habitable(to)) {
    return false;
  }

  const bridgeFrom = bridgeOn(from, pendingCounts);
  let here = from.elevation;
  if (mirrorOf(from) !== undefined && bridgeFrom !== 0) {
    if ((bridgeFrom & (1 << direction)) === 0) return false;
    here = mirrorOf(from)!;
  }
  return Math.abs(there - here) < MAX_STEP;
}

/** Hex distance, on the cube coordinates the axial pair stands for. */
export function hexDistance(a: Axial, b: Axial): number {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  return (Math.abs(dq) + Math.abs(dq + dr) + Math.abs(dr)) / 2;
}

/**
 * As far as a route is planned from the cursor, in cells. Twin of the server's
 * limit, so a preview is never drawn for a click that would come back `tooFar`.
 */
export const MAX_ROUTE_CELLS = 192;

/**
 * How many cells the preview search may look at before giving up.
 *
 * Well under the server's 30 000: this runs when the cursor crosses a cell
 * boundary, which is many times a second while the mouse is moving, and it
 * shares its frame with a raycast and everything else. A search that gives up
 * costs nothing visible — the click still goes to the server, which has the
 * whole map and a budget to match.
 */
const WORK_LIMIT = 6000;

interface Step {
  cell: Axial;
  cost: number;
  estimate: number;
}

/** A tiny binary heap, ordered on the A* estimate. */
class Frontier {
  private items: Step[] = [];

  push(step: Step): void {
    this.items.push(step);
    let index = this.items.length - 1;
    while (index > 0) {
      const parent = (index - 1) >> 1;
      if (this.items[parent].estimate <= this.items[index].estimate) break;
      [this.items[parent], this.items[index]] = [this.items[index], this.items[parent]];
      index = parent;
    }
  }

  pop(): Step | undefined {
    const top = this.items[0];
    const last = this.items.pop();
    if (this.items.length > 0 && last) {
      this.items[0] = last;
      let index = 0;
      for (;;) {
        const left = index * 2 + 1;
        const right = left + 1;
        let smallest = index;
        if (left < this.items.length && this.items[left].estimate < this.items[smallest].estimate) {
          smallest = left;
        }
        if (right < this.items.length && this.items[right].estimate < this.items[smallest].estimate) {
          smallest = right;
        }
        if (smallest === index) break;
        [this.items[smallest], this.items[index]] = [this.items[index], this.items[smallest]];
        index = smallest;
      }
    }
    return top;
  }

  get size(): number {
    return this.items.length;
  }
}

/**
 * A route from one cell to another over the terrain the client has, or null.
 *
 * The cost of a step is the **time it takes**, exactly as on the server, so the
 * line drawn round a thicket is drawn round it for the same reason the server
 * would go round it. Returns the cells to walk, not including where we stand.
 *
 * Null means "not from here, not with what is loaded": off the map, too far,
 * unreachable, or the search ran out of budget. A preview that is missing is
 * better than one that is wrong, and the click behind it still asks the server.
 */
/**
 * Czym ta trasa ma być: marszem czy drogą do położenia.
 *
 * Jedna funkcja, bo to jedno pytanie — którędy najszybciej — i dwie różniłyby
 * się dokładnie w chwili, w której gracz porównuje jedną z drugą. Droga ma
 * jedną regułę więcej: nie kładzie się wzdłuż rzeki.
 */
export type RouteKind = 'walk' | 'road' | 'sail';

/**
 * Czy kadłub przejdzie z pola na pole — bliźniak `players::step_by`
 * w trybie `Passage::Hull`.
 *
 * **Ten sam akwen, a nie samo „woda"**: lustro jeziora stoi na własnym poziomie,
 * więc jezioro obok morza to dwa akweny, a krok między nimi byłby wodospadem.
 * Dno nie liczy się wcale — statku nie obchodzi, jak głęboko pod nim jest — i
 * **most nie zatrzymuje**, bo przepływa się pod przęsłem.
 */
export function sailable(
  from: HexCellData | undefined,
  to: HexCellData | undefined
): boolean {
  if (!from || !to) return false;
  const here = mirrorOf(from);
  const there = mirrorOf(to);
  return here !== undefined && there !== undefined && here === there;
}

/**
 * Tempo statku wobec kroku człowieka — bliźniak `ships.pace` z katalogu.
 *
 * **Ten sam dług, co `ROAD_PACE`**: podgląd trasy musi mierzyć czas tak, jak
 * zmierzy go serwer, więc zmiana po tamtej stronie jest zmianą obu.
 */
const SAIL_PACE = 0.75;

/**
 * Jak często budzi się pętla ruchu na serwerze — bliźniak `players::TICK`.
 */
const TICK_SECONDS = 0.05;

/**
 * Ile naprawdę trwa krok statku.
 *
 * **Dosunięte do taktu serwera, i to nie jest kosmetyka.** Krok, który w takt
 * nie trafia, czeka tam do następnego przebudzenia — a wygładzenie liczone tutaj
 * z surowego ilorazu kończyłoby się wcześniej i statek stawałby przed każdym
 * polem. Liczone tym samym rachunkiem, co `ships::sailing_step`, żeby obie
 * strony wychodziły na tę samą liczbę.
 */
export const SAIL_STEP_SECONDS =
  Math.max(1, Math.round(STEP_SECONDS / SAIL_PACE / TICK_SECONDS)) * TICK_SECONDS;

export function findRoute(
  world: Map<number, HexCellData>,
  from: Axial,
  to: Axial,
  kind: RouteKind = 'walk'
): Axial[] | null {
  const start = world.get(axialKey(from.q, from.r));
  const goal = world.get(axialKey(to.q, to.r));
  const road = kind === 'road';
  const sail = kind === 'sail';
  // Kadłub ma to odwrotnie niż piechur: celem jest woda, a ląd jest tym, czego
  // nie ma jak dotknąć.
  if (sail) {
    if (!start || !sailable(start, goal)) return null;
  } else if (!start || !(habitable(goal) || bridgeOn(goal, road) !== 0)) {
    // Na gotowym moście wolno stanąć; na kładzionej drodze — także na tym
    // w budowie.
    return null;
  }
  if (from.q === to.q && from.r === to.r) return null;
  if (hexDistance(from, to) > MAX_ROUTE_CELLS) return null;

  const came = new Map<number, { cell: Axial; from: number }>();
  const best = new Map<number, number>();
  const frontier = new Frontier();

  const startKey = axialKey(from.q, from.r);
  best.set(startKey, 0);
  // Never an overestimate, and the fastest step there is now a paved one — the
  // same floor the server's search uses, and for the same reason: an estimate
  // above the true cost makes A* find *a* way rather than the fastest one.
  const floor = sail ? SAIL_STEP_SECONDS : STEP_SECONDS / ROAD_PACE;
  frontier.push({ cell: from, cost: 0, estimate: hexDistance(from, to) * floor });

  let seen = 0;
  while (frontier.size > 0) {
    const step = frontier.pop()!;
    const key = axialKey(step.cell.q, step.cell.r);
    if (step.cost > (best.get(key) ?? Infinity)) continue;

    if (step.cell.q === to.q && step.cell.r === to.r) {
      const route: Axial[] = [];
      let at = key;
      while (at !== startKey) {
        const previous = came.get(at)!;
        route.push(previous.cell);
        at = previous.from;
      }
      return route.reverse();
    }

    if (++seen > WORK_LIMIT) return null;

    const here = world.get(key);
    for (let direction = 0; direction < 6; direction++) {
      const next = neighbor(step.cell, direction);
      const nextKey = axialKey(next.q, next.r);
      const there = world.get(nextKey);
      if (sail ? !sailable(here, there) : !passable(here, there, direction, road)) continue;
      // A road may cross a river and may not run along it: the edge it would
      // use is the edge the water already uses.
      if (road && alongRiver(here, direction)) continue;

      // Paid on arrival, like the server: standing on rough ground is what
      // delays the next step, not walking off it.
      // Woda nie ma lasu ani kamienia, więc krok po niej jest jedną liczbą:
      // morze jest wszędzie takie samo.
      const cost = step.cost + (sail ? SAIL_STEP_SECONDS : crossingSeconds(there));
      if (cost >= (best.get(nextKey) ?? Infinity)) continue;

      best.set(nextKey, cost);
      came.set(nextKey, { cell: next, from: key });
      frontier.push({
        cell: next,
        cost,
        estimate: cost + hexDistance(next, to) * floor,
      });
    }
  }

  return null;
}
